<template>
  <div
    :id="addOn.code"
    class="slide"
    :class="{ selected: selectedAddOnData, 'have-error': haveError }"
  >
    <div class="slide-image">
      <picture
        v-lazyload="{
          src: addOn.addOnImage,
          updateCallback: imageLoadedEvent,
          canLoad,
          set: [
            { width: '800', height: '600', breakpoint: 'xs' },
            { width: '1024', height: '768', breakpoint: 'md' },
            { width: '1280', height: '800', breakpoint: 'lg' },
          ],
          fit: 'crop',
          align: 'center',
        }"
        class="image-wrapper"
      >
        <img
          class="image"
          src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
          :alt="addOn.addOnName"
        >
      </picture>
      <div class="slide-image-gradient-overlay" />
    </div>
    <div class="slide-content">
      <div class="slide-titles">
        <div class="slide-title">
          {{ addOn.addOnName }}
        </div>
        <div class="slide-price">
          {{ getPriceLabel }}
        </div>
      </div>
      <div class="slide-utility">
        <div class="slide-ctas">
          <button
            :id="`see-details-${addOn.sitecoreId}`"
            target="_self"
            class="slide-ctas--primary"
            tabindex="0"
            :aria-label="`${tc('booking.seeDetails')} ${addOn.addOnName}`"
            @click.prevent="handleCtaClick('see-details')"
            @keypress.enter.prevent="handleCtaClick('see-details')"
            @keypress.space.prevent="handleCtaClick('see-details')"
          >
            {{ haveError ? tc('enhancements.actionRequired') : tc('booking.seeDetails') }}
          </button>
          <button
            :id="`add-cta-${addOn.sitecoreId}`"
            class="slide-ctas--add"
            :class="{ selected: selectedAddOnData }"
            :disabled="isDisabled"
            :aria-disabled="isDisabled"
            :aria-pressed="selectedAddOnData ? 'true' : 'false'"
            :aria-label="selectedAddOnData
              ? `${tc('booking.remove')} ${addOn.addOnName}`
              : `${tc('booking.add')} ${addOn.addOnName}`"
            @click.prevent="handleCtaClick('add', $event)"
            @keypress.enter.prevent="handleCtaClick('add', $event)"
            @keypress.space.prevent="handleCtaClick('add', $event)"
          >
            <div
              v-if="haveError"
              class="icon-warning-red"
            />
            <div
              v-else-if="selectedAddOnData"
              :class="`icon-${isLoading && cartPending ? 'loading-black' : 'checkmark'}`"
            />
            <div
              v-else
              :class="`icon-${isLoading && cartPending ? 'loading' : 'add'}`"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex'

  export default {
    name: 'BookingAddOnsSlide',
    props: {
      addOn: {
        type: Object,
        required: true
      },
      canLoad: {
        type: Boolean,
        required: true
      },
      imageLoadedEvent: {
        type: Function,
        required: true
      },
    },
    data() {
      return {
        isLoading: false
      }
    },
    computed: {
      ...mapState('cart', ['cartPending']),
      ...mapState('auth', ['addOnsComponentData']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['getDisplayPrice']),
      ...mapGetters('cart', ['addOnsInRoom']),
      ...mapGetters('booking', ['showPricesWithTaxes']),
      selectedAddOnData() {
        return this.addOnsInRoom?.find(
          (e) => e.sitecoreId === this.addOn.sitecoreId
        )
      },
      isDisabled() {
        return this.cartPending
      },
      priceWithTaxes() {
        if (this.addOn?.airportAddOn) {
          const minPrice = this.addOn.vehicleDetails.reduce((min, vehicle) => {
            vehicle.price.forEach((price) => {
              if (price.base?.amountAfterTaxField) {
                const currentPrice = price.base.amountAfterTaxField
                if (currentPrice < min) {
                  min = currentPrice
                }
              }
            })
            return min
          }, Number.MAX_VALUE)

          return minPrice !== Number.MAX_VALUE ? minPrice : 0.00
        }
        return this.addOn?.price?.length > 0 ? this.addOn?.price[0]?.base?.amountAfterTaxField : 0.00
      },
      priceWithoutTaxes() {
        if (this.addOn?.airportAddOn) {
          const minPrice = this.addOn.vehicleDetails.reduce((min, vehicle) => {
            vehicle.price.forEach((price) => {
              if (price.base?.amountBeforeTaxField) {
                const currentPrice = price.base.amountBeforeTaxField
                if (currentPrice < min) {
                  min = currentPrice
                }
              }
            })
            return min
          }, Number.MAX_VALUE)

          return minPrice !== Number.MAX_VALUE ? minPrice : 0.00
        }
        return this.addOn?.price?.length > 0 ? this.addOn?.price[0]?.base?.amountBeforeTaxField : 0.00
      },
      getPriceLabel() {
        const priceFormatted = this.getDisplayPrice({ price: this.showPricesWithTaxes() ? this.priceWithTaxes : this.priceWithoutTaxes })
        if (this.addOn.airportAddOn) {
          return this.addOnsComponentData?.AirportPriceLabel?.replace('{{price}}', priceFormatted)
        }
        const addOnTypeLabel = this.addOnsComponentData?.ServicePricingType[this.addOn.servicePricingType]
        return `${priceFormatted} ${addOnTypeLabel}`
      },
      haveError() {
        if (!this.isBookingEditMode) return false
        if (!this.selectedAddOnData) {
          return false
        }
        const hasAirportAddOnWithoutData = this.selectedAddOnData?.airportAddOn
          && Object.keys(this.selectedAddOnData?.data || {}).length === 0
        const hasAvailableDatesWithoutSelected = (this.addOn?.availableDates?.length > 0)
          && Object.keys(this.selectedAddOnData?.data?.selectedAddOnDates || {}).length === 0

        return (hasAirportAddOnWithoutData || hasAvailableDatesWithoutSelected) && this.addOn?.servicePricingType !== 'per-night'
      },
      isFeesMessageVisible() {
        return this.addOn.showPopUpForRemovingAddOnSbe
      }
    },
    watch: {
      cartPending(val) {
        if (!val) {
          this.isLoading = val
        }
      },
      selectedAddOnData(newValue) {
        if (!newValue) {
          this.$nextTick(() => {
            const button = document.getElementById(`add-cta-${this.addOn.sitecoreId}`)
            if (button) {
              button.focus()
            }
          })
        }
      }
    },
    methods: {
      ...mapActions('cart', ['addAddOnToRoom', 'removeAddOnFromRoom']),
      handleCtaClick(action = null) {
        if (this.selectedAddOnData && action === 'see-details') {
          this.$emit('edit-add-on', this.selectedAddOnData)
        } else if (this.selectedAddOnData) {
          const openCancelModal = this.isFeesMessageVisible ? this.haveError : false
          this.$emit('remove-add-on', { selectedAddOnData: this.selectedAddOnData, openCancelModal })
        } else {
          this.$emit('select-add-on', this.addOn)
        }
      },
    }
  }
</script>
